import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { faChartBar } from '@fortawesome/pro-regular-svg-icons/faChartBar';
import { faRulerCombined } from '@fortawesome/pro-regular-svg-icons/faRulerCombined';
import { faTemperatureHigh } from '@fortawesome/pro-regular-svg-icons/faTemperatureHigh';
import { faWifi } from '@fortawesome/pro-regular-svg-icons/faWifi';
import {
  BROADBAND_OPTIONS,
  EPC_OPTIONS,
  FLOOR_SIZE_OPTIONS,
  HEATING_OPTIONS,
  YEAR_BUILT_OPTIONS,
} from '@propertypal/shared/src/constants/filterOptions';
import { UseSearchState } from '@propertypal/shared/src/hooks/useSearchState';
import { SearchState } from '@propertypal/shared/src/reducers/search';
import { SearchParams } from '@propertypal/shared/src/types/search';
import { formatInternalArea, formatYearBuilt } from '@propertypal/shared/src/utils/search/inputFormating';
import Checkbox from '@propertypal/web-ui/src/inputs/Checkbox';
import React, { FunctionComponent } from 'react';
import { useTheme } from 'styled-components';
import { EPCWrapper, InputColumn, SmartColumn, SmartMultiSelect, StyledSelect } from './SearchBar.style';

interface Props {
  filters: SearchState['filters'];
  handleFilterChange: UseSearchState['handleFilterChange'];
  isNewHomes: boolean;
  searchParams?: SearchParams;
  isIE?: boolean;
  isMobile?: boolean;
}

const SmartInputs: FunctionComponent<Props> = ({
  filters,
  handleFilterChange,
  isNewHomes,
  searchParams,
  isIE,
  isMobile,
}) => {
  const theme = useTheme();
  const labelStyle = { fontSize: 14, color: theme.primary };

  return (
    <>
      {!isIE && (
        <EPCWrapper>
          <StyledSelect
            ariaLabel="Select EPC Rating"
            label="EPC Rating"
            labelStyle={{ fontSize: 14, color: theme.primary }}
            id="epcRating"
            options={EPC_OPTIONS}
            value={filters.epc}
            placeholder="Select EPC Rating"
            onChange={handleFilterChange('epc')}
            testID="epcRating"
            icon={isMobile ? undefined : faChartBar}
          />

          <Checkbox
            containerStyle={{ marginTop: 10 }}
            label="Include Potential EPC Ratings"
            name="epcRatingType"
            value={filters.includePotentialEpc}
            onValueChange={handleFilterChange('includePotentialEpc')}
            disabled={!filters.epc}
          />
        </EPCWrapper>
      )}

      <InputColumn style={{ flexWrap: 'wrap', gap: '10px', alignItems: 'flex-start', marginTop: 30 }}>
        <SmartColumn>
          <SmartMultiSelect
            label="Select Heating Type"
            labelStyle={labelStyle}
            id="heatingType"
            placeholder="Select Heating Type"
            options={HEATING_OPTIONS}
            values={filters.heatingType || []}
            onChange={handleFilterChange('heatingType')}
            icon={isMobile ? undefined : faTemperatureHigh}
            testID="heatingType"
          />
        </SmartColumn>

        {!isNewHomes && (
          <SmartColumn>
            <StyledSelect
              ariaLabel="Select Year Built"
              label="Year Built"
              labelStyle={labelStyle}
              id="yearBuilt"
              options={YEAR_BUILT_OPTIONS}
              value={filters.yearBuilt}
              placeholder="Select Year Built"
              onChange={handleFilterChange('yearBuilt')}
              testID="yearBuilt"
              icon={isMobile ? undefined : faCalendar}
              wildcard={formatYearBuilt(searchParams?.minYearBuilt, searchParams?.maxYearBuilt)}
            />
          </SmartColumn>
        )}

        <SmartColumn>
          <StyledSelect
            ariaLabel="Select Floor Size"
            label="Floor Size"
            labelStyle={labelStyle}
            id="floorSize"
            options={FLOOR_SIZE_OPTIONS}
            value={filters.minInternalArea}
            placeholder="Select Floor Size"
            onChange={handleFilterChange('minInternalArea')}
            testID="floorSize"
            icon={isMobile ? undefined : faRulerCombined}
            wildcard={formatInternalArea}
          />
        </SmartColumn>

        {!isIE && (
          <SmartColumn>
            <StyledSelect
              ariaLabel="Select Broadband Speed"
              label={`Broadband Speed${!isMobile ? ' Availability' : ''}`}
              labelStyle={labelStyle}
              id="broadband"
              options={BROADBAND_OPTIONS}
              value={filters.broadband}
              placeholder={`${!isMobile ? 'Select ' : ''}Broadband Speed`}
              onChange={handleFilterChange('broadband')}
              testID="broadband"
              icon={isMobile ? undefined : faWifi}
            />
          </SmartColumn>
        )}
      </InputColumn>
    </>
  );
};

export default SmartInputs;
